import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, LeasingContact, Property } from "@/types"

interface Props {
  property: Property
  leasing_contact: LeasingContact
}

export default function Edit({ property, leasing_contact: contact }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: agentPropertiesPath() },
    { title: property.name, href: `/agent/properties/${property.id}` },
    { title: "Leasing Contacts", href: `/agent/properties/${property.id}/leasing_contacts` },
    { title: contact.name, href: `/agent/properties/${property.id}/leasing_contacts/${contact.id}/edit` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${contact.name}`} />

      <div className="max-w-2xl space-y-6 p-6">
        <h1 className="text-2xl font-semibold">Edit Leasing Contact</h1>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form
              method="patch"
              action={`/agent/properties/${property.id}/leasing_contacts/${contact.id}`}
              encType="multipart/form-data"
              className="space-y-4"
            >
              {({ errors, processing }) => (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" name="name" defaultValue={contact.name} />
                      <InputError messages={errors.name} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="title">Title</Label>
                      <Input id="title" name="title" defaultValue={contact.title ?? ""} />
                      <InputError messages={errors.title} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" defaultValue={contact.email ?? ""} />
                      <InputError messages={errors.email} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" name="phone" defaultValue={contact.phone ?? ""} />
                      <InputError messages={errors.phone} />
                    </div>
                  </div>

                  <div className="grid max-w-xs gap-2">
                    <Label htmlFor="position">Display Order</Label>
                    <Input id="position" name="position" type="number" defaultValue={contact.position} />
                    <InputError messages={errors.position} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="headshot">Headshot</Label>
                    <Input id="headshot" name="headshot" type="file" accept="image/*" />
                    {contact.headshot_url && (
                      <img src={contact.headshot_url} alt={contact.name} className="mt-2 h-24 w-24 rounded-full border object-cover" />
                    )}
                    <InputError messages={errors.headshot} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" disabled={processing}>Save Changes</Button>
                    <Button variant="outline" type="button" onClick={() => history.back()}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
